import { useCallback, useState } from 'react';
import { InventoryPanel, useOrganizationWithLatestReport } from '../inventory/InventoryPanel';
import { SampleReviewPanel } from './SampleReviewPanel';
import { SampleApproval } from './SampleApproval';
import { canDecideSubmission } from './decide';
import { ClientTagField, SubmissionTitleEditor } from './SubmissionInternalFields';
import { useCompanies } from '../tracker/hooks';
import { GenerateProductionPo } from '../cgOrders/GenerateProductionPo';
import { buildOrderPrefill, canIssuePo } from '../cgOrders/orderPrefill';
import { todayIso } from '../cgOrders/types';
import { canEditModule, useMyPermissions } from '../auth/useMyPermissions';
import { Link, useNavigate } from 'react-router';
import {
  Badge,
  Button,
  Dialog,
  EmptyState,
  InlineError,
  Input,
  Panel,
  Select,
  Skeleton,
  Textarea,
} from '../../components/ui';
import { PageHeader } from '../../layouts/Shells';
import { useAuth } from '../auth/AuthProvider';
import { useSupplierOrganizations } from '../supplierOrganizations/hooks';
import {
  useCreateHandoff,
  useDecideSubmission,
  useHandoffLinks,
  useHandoffs,
  useSetHandoffPo,
  useSubmissionPhotos,
  useSubmissions,
} from './hooks';
import {
  SUBMISSION_STATUS_LABELS,
  SUBMISSION_STATUS_TONES,
  exchangeErrorMessage,
  splitTags,
} from './types';
import type { SupplierHandoff, SupplierSubmission } from './types';

/**
 * The TAP side of the supplier exchange (the /supplier-reviews page):
 * incoming sample submissions to decide on, and handoffs sent out for
 * acknowledgement. Task-level supplier reviews continue to live inside each
 * task's panel; this page is the organization-level two-way door.
 */

function shortDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/**
 * Reviewing one sample run.
 *
 * Exported so the CG order page can open it. The run belongs to a job, and
 * until now the only way to reach it was a Sample Reviews tab filed beside two
 * other screens about the same job — Kyu's point, and the reason that tab is
 * going away.
 */
export function SubmissionDialog({
  submission,
  organizationName,
  onClose,
}: {
  submission: SupplierSubmission;
  organizationName: string;
  onClose: () => void;
}) {
  const photosQuery = useSubmissionPhotos(submission.id);
  const decide = useDecideSubmission();
  const { access } = useAuth();
  const permissions = useMyPermissions();
  // Decided HERE, from the one rule the database also applies, rather than
  // handed in by the caller. Two callers had two answers — the exchange page
  // said "tap_admin", the CG order page said "member on supplier_reviews" —
  // and the second drew Approve buttons the database then refused.
  const canDecide = canDecideSubmission(
    access.status === 'approved' ? access.profile : null,
    permissions.data,
  );

  // Renaming is any TAP user; tagging is sales only. The companies query is
  // gated on sales membership at the database, so a reviewer outside sales
  // simply receives nothing and sees the client as text instead of a picker.
  const viewer = access.status === 'approved' ? access.profile : null;
  const isTapUser = viewer?.account_type === 'tap';
  const isSalesMember = canEditModule(permissions.data, 'sales_tracker');
  const companiesQuery = useCompanies();
  const companies = (companiesQuery.data ?? []).map((company) => ({
    id: company.id,
    name: company.name,
  }));
  const actorId = viewer?.id ?? '';
  const [note, setNote] = useState('');
  const [error, setError] = useState('');
  const [confirmingApprove, setConfirmingApprove] = useState(false);

  const photos = photosQuery.data ?? [];
  const open = submission.status === 'submitted' || submission.status === 'revision_requested';

  function decideAs(status: 'approved' | 'revision_requested' | 'on_hold') {
    if (status === 'revision_requested' && note.trim() === '') {
      setError('Tell the supplier what to revise — the note is required.');
      return;
    }
    setError('');
    decide.mutate(
      { submissionId: submission.id, status, note },
      {
        onSuccess: onClose,
        onError: (cause) => setError(exchangeErrorMessage(cause.message)),
      },
    );
  }

  return (
    <Dialog
      open
      title={submission.title}
      onClose={onClose}
      wide
      footer={
        canDecide && open ? (
          <>
            {confirmingApprove ? (
              <>
                <Button disabled={decide.isPending} onClick={() => decideAs('approved')}>
                  {decide.isPending ? 'Approving…' : 'Confirm — creates the tracking project'}
                </Button>
                <Button variant="ghost" onClick={() => setConfirmingApprove(false)}>
                  Back
                </Button>
              </>
            ) : (
              <>
                <Button onClick={() => setConfirmingApprove(true)}>Approve for production</Button>
                <Button
                  variant="secondary"
                  disabled={decide.isPending}
                  onClick={() => decideAs('revision_requested')}
                >
                  Request revision
                </Button>
                <Button
                  variant="ghost"
                  disabled={decide.isPending}
                  onClick={() => decideAs('on_hold')}
                >
                  On hold
                </Button>
              </>
            )}
          </>
        ) : undefined
      }
    >
      {error !== '' && <InlineError>{error}</InlineError>}

      {/* Internal fields. A supplier never reaches this dialog — it is the TAP
          side of the exchange — so the only question is which TAP people may
          change what. */}
      <SubmissionTitleEditor
        submissionId={submission.id}
        title={submission.title}
        canRename={isTapUser}
      />
      <ClientTagField
        submissionId={submission.id}
        companies={companies}
        canTag={isSalesMember}
        taggedBy={actorId}
      />

      <p style={{ margin: '0 0 var(--space-3)', color: 'var(--text-muted)' }}>
        {organizationName} · sent {shortDate(submission.created_at)} ·{' '}
        <Badge tone={SUBMISSION_STATUS_TONES[submission.status]}>
          {SUBMISSION_STATUS_LABELS[submission.status]}
        </Badge>
      </p>
      {submission.notes !== '' && (
        <p style={{ margin: '0 0 var(--space-4)', fontSize: 'var(--font-size-sm)' }}>
          {submission.notes}
        </p>
      )}
      {/* Any TAP user may judge an OPTION — that matches the update policy on
          submission_options, and it is how a reviewer outside sales does the
          work. Deciding the submission as a whole is canDecide above: the
          tap_admin role, or Admin on Supplier Reviews in the matrix. Once
          approved, the run carries the stamp and every chosen photo the
          watermark (SampleApproval). */}
      <SampleApproval
        status={submission.status}
        decidedBy={submission.decided_by}
        decidedAt={submission.decided_at}
      >
        <SampleReviewPanel submissionId={submission.id} canReview={isTapUser === true} />
      </SampleApproval>

      {/* The flat photo list predates per-component options. Kept for
          submissions uploaded before the slots existed, and hidden once a
          submission has options, or the same images appear twice. */}
      {photos.length === 0 ? null : (
        <p className="lead-panel-section-title">Sample photos ({photos.length})</p>
      )}
      {/* No empty state here, deliberately. This block is the LEGACY flat
          photo list, kept only for submissions that predate component slots —
          and its old "supplier has not uploaded photos yet" sentence rendered
          under a panel showing four uploaded photos, flatly contradicting it.
          When the flat list is empty there is nothing to say: the slots above
          are the truth. */}
      {photos.length === 0 ? null : (
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 'var(--space-3)',
            marginBottom: 'var(--space-4)',
          }}
        >
          {photos.map((photo) => (
            <a key={photo.id} href={photo.url} target="_blank" rel="noreferrer">
              {photo.name === '' ? 'Photo' : photo.name}
            </a>
          ))}
        </div>
      )}
      {submission.decision_note !== '' && (
        <p style={{ margin: '0 0 var(--space-4)', fontSize: 'var(--font-size-sm)' }}>
          <strong>Decision note:</strong> {submission.decision_note}
        </p>
      )}
      {submission.project_id !== null && (
        <p style={{ margin: '0 0 var(--space-4)' }}>
          <Link to={`/projects/${submission.project_id}`}>Open the tracking project →</Link>
        </p>
      )}
      {canDecide && open && (
        <Textarea
          label="Note to the supplier (required for revisions)"
          rows={2}
          value={note}
          onChange={(event) => setNote(event.target.value)}
        />
      )}
    </Dialog>
  );
}

function NewHandoffDialog({ onClose }: { onClose: () => void }) {
  const organizationsQuery = useSupplierOrganizations();
  const create = useCreateHandoff();
  const [organizationId, setOrganizationId] = useState('');
  const [title, setTitle] = useState('');
  const [notes, setNotes] = useState('');
  const [tags, setTags] = useState('');
  const [client, setClient] = useState('');
  const [productType, setProductType] = useState('');
  const [description, setDescription] = useState('');
  const [units, setUnits] = useState('');
  const [linksText, setLinksText] = useState('');
  const [error, setError] = useState('');

  const organizations = (organizationsQuery.data ?? []).filter((org) => org.is_active);

  function submit() {
    if (organizationId === '') {
      setError('Pick the supplier organization first.');
      return;
    }
    if (title.trim() === '') {
      setError('Give the handoff a title.');
      return;
    }
    // One link per line: the URL, optionally followed by a label.
    const links = linksText
      .split('\n')
      .map((line) => line.trim())
      .filter((line) => line !== '')
      .map((line) => {
        const [url = '', ...rest] = line.split(/\s+/);
        return { url, name: rest.join(' ') };
      });
    const badLink = links.find((link) => !link.url.startsWith('https://'));
    if (badLink !== undefined) {
      setError(`Links must start with https:// — check "${badLink.url}".`);
      return;
    }
    setError('');
    create.mutate(
      {
        supplierOrganizationId: organizationId,
        title,
        notes,
        tags,
        client,
        productType,
        description,
        units: units.trim() === '' ? null : Math.max(1, Math.round(Number(units) || 1)),
        links,
      },
      {
        onSuccess: onClose,
        onError: (cause) => setError(exchangeErrorMessage(cause.message)),
      },
    );
  }

  return (
    <Dialog
      open
      title="Send for acknowledgement"
      onClose={onClose}
      wide
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={create.isPending}>
            {create.isPending ? 'Sending…' : 'Send to supplier'}
          </Button>
        </>
      }
    >
      {error !== '' && <InlineError>{error}</InlineError>}
      <Select
        label="Supplier organization"
        value={organizationId}
        options={[
          { value: '', label: 'Pick an organization…' },
          ...organizations.map((org) => ({ value: org.id, label: org.name })),
        ]}
        onChange={(event) => setOrganizationId(event.target.value)}
      />
      <Input
        label="Title"
        placeholder="Final mockups — Mesh Bag v2"
        value={title}
        onChange={(event) => setTitle(event.target.value)}
      />
      <Textarea
        label="Notes"
        rows={3}
        value={notes}
        onChange={(event) => setNotes(event.target.value)}
      />
      <Input
        label="Tags (comma separated)"
        placeholder="mesh bag, holiday 2026"
        value={tags}
        onChange={(event) => setTags(event.target.value)}
      />
      {/* The order block. None of it reaches the supplier — the guard trigger
          refuses a supplier write to all four, and the portal never renders
          them. They exist so that creating the CG order from this handoff is
          a confirmation rather than a re-typing exercise. */}
      <fieldset className="handoff-order-fields">
        <legend>For the CG order (not shown to the supplier)</legend>
        <Input
          label="Client"
          placeholder="Attenzione AS"
          value={client}
          onChange={(event) => setClient(event.target.value)}
        />
        <Input
          label="Product type"
          placeholder="Beaded Bracelet"
          value={productType}
          onChange={(event) => setProductType(event.target.value)}
        />
        <Input
          label="Description"
          placeholder="Custom Hidden Sea Bracelets"
          value={description}
          onChange={(event) => setDescription(event.target.value)}
        />
        <Input
          label="Units"
          placeholder="300"
          value={units}
          onChange={(event) => setUnits(event.target.value)}
        />
      </fieldset>
      <Textarea
        label="Links — one Drive folder link works best; or several links, one per line with an optional label"
        rows={3}
        placeholder={'https://drive.google.com/drive/folders/… All assets for this order'}
        value={linksText}
        onChange={(event) => setLinksText(event.target.value)}
      />
      <p style={{ margin: 0, fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
        The supplier sees this in their portal, reviews it, and acknowledges — they cannot change
        it.
      </p>
    </Dialog>
  );
}

/**
 * Materials inventory, with a supplier picker.
 *
 * Exported so CG Orders can render it as its own tab. It used to live only
 * under Supplier Reviews, which filed it by SYSTEM — a screen about stock,
 * next to a screen about handoffs, next to a screen about samples, none of
 * them next to the orders they are all about.
 */
export function InventorySupplierPicker({
  supplierId,
  onSupplierChange,
}: {
  supplierId: string;
  onSupplierChange: (id: string) => void;
}) {
  // Fetches for itself rather than being handed a list: the query is cached
  // anyway. The SELECTION, though, is owned by the page: this component
  // unmounts when you leave the tab, and state held here would silently reset
  // the chosen supplier every time somebody looked at another tab.
  const organizationsQuery = useSupplierOrganizations();
  const organizations = (organizationsQuery.data ?? []).filter((org) => org.is_active);

  // Land on the supplier that actually has a report rather than whichever
  // sorts first by name. Opening on an empty supplier is how an uploaded
  // report came to look invisible to everyone but the person who uploaded it.
  const withReport = useOrganizationWithLatestReport();
  const fallback =
    withReport.data !== null && organizations.some((org) => org.id === withReport.data)
      ? withReport.data
      : (organizations[0]?.id ?? '');
  const chosen = supplierId === '' ? (fallback ?? '') : supplierId;
  if (organizations.length === 0) {
    return <p className="task-panel-text">No active suppliers yet.</p>;
  }
  return (
    <>
      {organizations.length === 1 ? null : (
        <Select
          label="Supplier"
          value={chosen}
          options={organizations.map((org) => ({ value: org.id, label: org.name }))}
          onChange={(event) => onSupplierChange(event.target.value)}
        />
      )}
      <InventoryPanel supplierOrganizationId={chosen} canUpload />
    </>
  );
}

/**
 * One tab now, and it is about to be one page.
 *
 * This screen carried three: Materials Inventory, Design Handoffs, Sample
 * Reviews. Three of OUR filing categories in a tab of their own, while the
 * thing all three were about — an order — lived somewhere else. Kyu named it,
 * and it is the same fault the supplier portal had.
 *
 *   Materials Inventory  →  CG Orders → Inventory, beside the orders it is
 *                           a question about
 *   Sample Reviews       →  the order page, as a section of the job
 *   Design Handoffs      →  stays HERE, and also appears on the order page
 *
 * Handoffs stay because a handoff can exist BEFORE an order does — a design
 * goes to a factory for acknowledgement, and the PO follows. Retiring this
 * screen outright would leave the only way to create one behind a route with
 * no link to it.
 */
type ExchangeTab = 'handoffs';

const TABS: ExchangeTab[] = ['handoffs'];

const TAB_LABELS: Record<ExchangeTab, string> = {
  handoffs: 'Design Handoffs',
};

export function SupplierExchangePage() {
  const submissionsQuery = useSubmissions();
  const handoffsQuery = useHandoffs();
  const organizationsQuery = useSupplierOrganizations();
  const [openSubmissionId, setOpenSubmissionId] = useState<string | null>(null);
  const [creatingHandoff, setCreatingHandoff] = useState(false);
  const [generatingPo, setGeneratingPo] = useState<SupplierHandoff | null>(null);
  const navigate = useNavigate();
  const [tab, setTab] = useState<ExchangeTab>('handoffs');

  const closeSubmission = useCallback(() => setOpenSubmissionId(null), []);
  const closeHandoff = useCallback(() => setCreatingHandoff(false), []);

  const submissions = submissionsQuery.data ?? [];
  const handoffs = handoffsQuery.data ?? [];
  const organizationName = (id: string) =>
    (organizationsQuery.data ?? []).find((org) => org.id === id)?.name ?? '—';
  const openSubmission = submissions.find((s) => s.id === openSubmissionId) ?? null;

  return (
    <>
      {/* The description stays, and is the clearest case for keeping one:
          most of it is a signpost to two other pages. */}
      <PageHeader
        title="Design Handoffs"
        topbar
        description="Designs sent to a factory for acknowledgement, before the PO exists. Sample reviews and the rest of a job's history live on the order's own page; materials inventory is under CG Orders."
      />

      <div className="tracker-tab-nav" role="group" aria-label="Supplier workflow">
        {TABS.map((key) => (
          <button
            key={key}
            type="button"
            className={`tracker-tab-button${tab === key ? ' tracker-tab-button--active' : ''}`}
            aria-pressed={tab === key}
            onClick={() => setTab(key)}
          >
            {TAB_LABELS[key]}
          </button>
        ))}
      </div>

      {tab !== 'handoffs' ? null : (
        <Panel
          title="Sent for acknowledgement"
          actions={
            <Button onClick={() => setCreatingHandoff(true)}>Send for acknowledgement</Button>
          }
        >
          {handoffsQuery.isPending ? (
            <Skeleton lines={3} label="Loading" />
          ) : handoffs.length === 0 ? (
            <EmptyState
              title="Nothing sent yet"
              description="Send final client-approved designs to a supplier; they acknowledge in their portal."
            />
          ) : (
            <div className="tracker-table-scroll">
              <table className="tracker-table">
                <thead>
                  <tr>
                    <th>Handoff</th>
                    <th>Supplier</th>
                    <th>Tags</th>
                    <th>Status</th>
                    <th>Production PO</th>
                  </tr>
                </thead>
                <tbody>
                  {handoffs.map((handoff) => (
                    <tr key={handoff.id}>
                      <td style={{ fontWeight: 'var(--font-weight-medium)' }}>{handoff.title}</td>
                      <td style={{ color: 'var(--text-secondary)' }}>
                        {organizationName(handoff.supplier_organization_id)}
                      </td>
                      <td>
                        {splitTags(handoff.tags).map((tag) => (
                          <Badge key={tag} tone="neutral">
                            {tag}
                          </Badge>
                        ))}
                      </td>
                      <td>
                        {handoff.acknowledged_at === null ? (
                          <Badge tone="warning">Awaiting acknowledgement</Badge>
                        ) : (
                          <span>
                            <Badge tone="success">Acknowledged</Badge>
                            {handoff.ack_note !== '' && (
                              <span
                                style={{
                                  marginLeft: 'var(--space-2)',
                                  color: 'var(--text-secondary)',
                                  fontSize: 'var(--font-size-xs)',
                                }}
                              >
                                “{handoff.ack_note}”
                              </span>
                            )}
                          </span>
                        )}
                      </td>
                      <td>
                        {/* Only an acknowledged handoff gets the action: a PO
                            tells a factory to start, and the acknowledgement is
                            them confirming they have the design to start from. */}
                        {handoff.acknowledged_at === null ? (
                          <span
                            style={{
                              color: 'var(--text-muted)',
                              fontSize: 'var(--font-size-xs)',
                            }}
                          >
                            Needs acknowledgement first
                          </span>
                        ) : (
                          <div className="handoff-po-actions">
                            {/* The new path creates the order FROM the handoff.
                                The old one links to an order that already
                                exists — still the right move when the order was
                                raised first, so both stay. */}
                            {canIssuePo(handoff) ? (
                              <Button
                                size="sm"
                                onClick={() => {
                                  void navigate('/cg-orders', {
                                    state: {
                                      prefill: buildOrderPrefill(handoff, todayIso()),
                                    },
                                  });
                                }}
                              >
                                Issue PO &amp; Create CG Order
                              </Button>
                            ) : null}
                            <Button
                              variant="secondary"
                              size="sm"
                              onClick={() => setGeneratingPo(handoff)}
                            >
                              {handoff.handoff_po_id === null ? 'Generate PO' : 'View PO'}
                            </Button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Panel>
      )}

      {generatingPo === null ? null : (
        <HandoffPoDialog handoff={generatingPo} onClose={() => setGeneratingPo(null)} />
      )}

      {openSubmission !== null && (
        <SubmissionDialog
          submission={openSubmission}
          organizationName={organizationName(openSubmission.supplier_organization_id)}
          onClose={closeSubmission}
        />
      )}
      {creatingHandoff && <NewHandoffDialog onClose={closeHandoff} />}
    </>
  );
}

/**
 * Wraps the PO generator with what only the exchange knows: which supplier is
 * making it, and the design files that were handed over. The links go on the
 * PO so "Design files" is a real anchor the factory can open — the sample
 * printed the literal word "link" instead.
 */
function HandoffPoDialog({ handoff, onClose }: { handoff: SupplierHandoff; onClose: () => void }) {
  const organizations = useSupplierOrganizations();
  const links = useHandoffLinks(handoff.id);
  const setPo = useSetHandoffPo();

  const organization = (organizations.data ?? []).find(
    (row) => row.id === handoff.supplier_organization_id,
  );

  return (
    <GenerateProductionPo
      manufacturer={organization?.name ?? 'Supplier'}
      attn={organization?.contact_name ?? ''}
      address={organization?.address ?? ''}
      designLinks={(links.data ?? []).map((link) => ({ url: link.url, name: link.name }))}
      linkedOrderId={handoff.handoff_po_id}
      isLinking={setPo.isPending}
      onLink={(orderId) => setPo.mutate({ handoffId: handoff.id, orderId })}
      onClose={onClose}
    />
  );
}
